import React, { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { Box } from "@mui/system";
import { Typography, TextField } from "@mui/material";
import { withStyles } from "@mui/styles";
import { getLocalStorage } from "./helpers";
import { addScoreToLeaderboard } from "./firebase/helpers";
import { blockedWords } from "./blockedWords";

const styles = (theme) => ({
  disabledButton: {
    backgroundColor: "gray !important",
    color: "white",
  },
});

const AlertDialog = ({
  alertDialogOpen,
  setAlertDialogOpen,
  prevWords,
  message,
  setAlertMessage,
  alertTitle,
  setAlertTitle,
  alertScore,
  setAlertScore,
  setPrevWords,
  beatPersonalBest,
  setBeatPersonalBest,
  copyToClipboard,
  confettiOn,
  setConfettiOn,
  classes,
  setLeaderboardOpen,
  setGoldConfettiOn,
  resetBoard
}) => {
  const [name, setName] = useState("");
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [scoreSavedToLeaderboard, setScoreSavedToLeaderboard] = useState(false);

  const handleClose = (reset) => {
    setAlertDialogOpen(false);
    setConfettiOn(false);
    setGoldConfettiOn(false);
    if (reset) {
      resetBoard();
    }
    setTimeout(() => {
      setScoreSavedToLeaderboard(false);
      setError(false);
      setErrorMessage("");
      setAlertMessage("");
      setAlertTitle("");
      setAlertScore("");
      setBeatPersonalBest("");
    }, 1000);
  };

  const handleAddScoreToLeaderboard = async () => {
    for (let i = 0; i < blockedWords.length; i++) {
      const lowercaseName = name.toLowerCase();
      if (lowercaseName.includes(blockedWords[i])) {
        setError(true);
        setErrorMessage("That name is not allowed.");
        return;
      }
    }
    try {
      setError(false);
      setErrorMessage("");
      setScoreSavedToLeaderboard(true);
      await addScoreToLeaderboard(alertScore, name, prevWords);
      setLeaderboardOpen(true);
    } catch (err) {
      setScoreSavedToLeaderboard(false);
      setError(true);
      setErrorMessage(getLeaderboardErrorMessage(err));
    }
  };

  const getLeaderboardErrorMessage = (err) => {
    switch (err?.code || err?.message) {
      case "invalid_name":
        return "Enter a valid name.";
      case "name_not_allowed":
        return "That name is not allowed.";
      case "invalid_words":
        return "Board data was invalid. Finish the game again.";
      case "word_not_allowed":
        return "One of those words is not allowed.";
      case "duplicate_word":
        return "Duplicate words are not allowed.";
      case "letter_reuse":
        return "A letter was reused in the same column.";
      case "invalid_score":
      case "score_mismatch":
        return "Score verification failed. Try again.";
      case "missing_settings":
      case "invalid_settings":
        return "Game settings are unavailable right now.";
      case "internal_error":
        return "Server error. Try again in a moment.";
      default:
        return "Could not submit score. Try again.";
    }
  };

  return (
    <Dialog
      open={alertDialogOpen}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{
        style: {
          backgroundColor: "rgb(30, 30, 30)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        },
      }}
      BackdropProps={{
        style: { backgroundColor: confettiOn ? "transparent" : "" },
      }}
    >
      {alertTitle && (
        <DialogTitle id="alert-dialog-title" style={{ color: "white" }}>
          {alertTitle}
        </DialogTitle>
      )}
      <DialogContent>
        <DialogContentText
          id="alert-dialog-description"
          style={{ color: "#b0b0b0", textAlign: "center" }}
        >
          {message}
        </DialogContentText>
        {alertScore && (
          <>
            <Box>
              <Typography
                variant="h2"
                style={{ color: "white", textAlign: "center" }}
              >
                {alertScore}
              </Typography>
            </Box>
            <Box textAlign="center">
              {beatPersonalBest ? (
                <Box>
                  <Typography variant="h6" style={{ color: "#a6a6a6" }}>
                    New personal best!
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Typography variant="h6" style={{ color: "#a6a6a6" }}>
                    {`Personal best: ${getLocalStorage("personalBest") || 0}`}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
        {alertScore && (
          <Box
            marginTop="16px"
            width="260px"
            display="flex"
            flexDirection="column"
          >
            <Box marginBottom="8px">
              <Typography style={{ color: "gray", fontSize: "12px" }}>
                Your name:
              </Typography>
              <TextField
                error={error}
                helperText={errorMessage}
                FormHelperTextProps={{
                  style: { color: "#ff8a8a", marginLeft: 0 },
                }}
                sx={{ input: { color: "white" } }}
                inputProps={{ maxLength: 20 }}
                disabled={scoreSavedToLeaderboard}
                margin="none"
                size="small"
                hiddenLabel
                variant="filled"
                style={{ backgroundColor: "#333333", color: "white" }}
                fullWidth
                onChange={(e) => {
                  setName(e.target.value);
                  if (error) setError(false);
                }}
              />
            </Box>
            <Button
              classes={{ disabled: classes.disabledButton }}
              disabled={!name || scoreSavedToLeaderboard}
              onClick={() => handleAddScoreToLeaderboard()}
              autoFocus
              variant="contained"
              color="success"
              fullWidth
            >
              ADD SCORE TO LEADERBOARD
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {alertScore && (
          <Button
            onClick={() => copyToClipboard()}
            autoFocus
            variant="contained"
            color="primary"
          >
            SHARE
          </Button>
        )}
        {alertScore && (
          <Button
            onClick={() => handleClose(true)}
            autoFocus
            variant="contained"
            color="success"
          >
            PLAY AGAIN
          </Button>
        )}
        <Button onClick={() => handleClose()} autoFocus variant="contained">
          CLOSE
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withStyles(styles)(AlertDialog);
